<template>
  <header
    class="flex content-center justify-between px-24 pb-12 pt-24 md:justify-start"
  >
    <h2 class="heading-2 mb-10">{{ tableTitle }}</h2>
    <span class="ml-20 grid gap-1">
      <p v-if="count > MAX_CSV_ORDERS_COUNT" class="max-w-380">
        {{ i18n(translations.cantExportLabel) }}
      </p>
      <RevButton
        v-if="showCsvExport"
        aria-labelledby="scrooge"
        class="w-max"
        data-qa="export"
        :disabled="!canExportOrders"
        :loading="isDownloading"
        size="small"
        variant="primary"
        @click="exportCSV"
      >
        {{ i18n(translations.export) }}
      </RevButton>
    </span>
  </header>

  <RevTable
    v-if="orders"
    :collection="orders"
    :columns="tableColumns"
    transparent-header
  >
    <template #body-country="{ item }">
      <Flag
        aria-hidden="true"
        class="mr-4"
        :country-code="getCountryCodeFromLocale(item.countryCode)"
      />
    </template>
    <template #body-order="{ item }">
      {{ item.sourcingOrderId }}
    </template>
    <template #body-trackingNumber="{ item }">
      <span class="break-all">
        {{ item.trackingNumber }}
      </span>
    </template>
    <template #body-customer="{ item }">
      {{ `${item.customer.firstName} ${item.customer.lastName}` }}
    </template>
    <template #body-product="{ item }">
      {{ item.product.title }}
    </template>
    <template #body-status="{ item }">
      <RevTag v-if="!isReceivedTab" v-bind="tag(item.state)" />
    </template>
    <template #body-remainingTime="{ item }">
      <RevTag
        v-if="isReceivedTab"
        class="md:max-w-[7rem] md:break-all lg:max-w-none lg:break-keep"
        :label="delay(item.remainingHours || 0)"
        variant="secondary"
      />
    </template>
    <template #body-moreDetails="{ item }">
      <RevButton
        size="small"
        variant="secondary"
        @click="redirect(item.sourcingOrderId)"
        >{{ i18n(translations.seeDetails) }}
      </RevButton>
    </template>
  </RevTable>
  <RevButton
    v-if="hasNextPage"
    class="m-32 flex"
    :loading="isLoadingMore"
    variant="secondary"
    @click="emit('load-next-page')"
  >
    {{ i18n(translations.viewMore) }}
  </RevButton>
</template>

<script setup lang="ts">
import { useRouter, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import { exportSourcingOrders } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import type { MerchantOrder } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'

import { ROUTE_NAMES } from '~/constants/routes'
import { getCountryCodeFromLocale } from '~/scopes/buyback/utils/buyback.utils'

import { useStatus } from '../../composables/useStatus'
import type OrderFilters from '../Filters/Filters.types'

import { MAX_CSV_ORDERS_COUNT } from './OrdersTable.constants'
import translations from './OrdersTable.translations'

const props = defineProps<{
  isLoadingMore: boolean
  isAllTab: boolean
  isReceivedTab: boolean
  isPendingTab: boolean
  orders: Array<MerchantOrder>
  hasNextPage: boolean
  count: number
  filters: OrderFilters
}>()
const emit = defineEmits(['load-next-page'])
const i18n = useI18n()
const logger = useLogger()
const { delay, tag } = useStatus()
const router = useRouter()
const { KILL_REFURBISHER_LISTING_EXPENSIVE } = useRuntimeConfig().public
const { openErrorToast } = useTheToast()

const isDownloading = ref(false)
const tableColumns = [
  {
    key: 'country',
    label: i18n(translations.country),
  },
  {
    key: 'order',
    label: i18n(translations.order),
  },
  {
    key: 'trackingNumber',
    label: i18n(translations.trackingNumber),
  },
  {
    key: 'customer',
    label: i18n(translations.customer),
  },
  {
    key: 'product',
    label: i18n(translations.product),
  },
  ...(!props.isReceivedTab
    ? [
        {
          key: 'status',
          label: i18n(translations.status),
        },
      ]
    : []),
  ...(props.isReceivedTab
    ? [
        {
          key: 'remainingTime',
          label: i18n(translations.remainingTime),
        },
      ]
    : []),
  {
    key: 'moreDetails',
    label: '',
  },
]

const tooMuchCSVOrders = computed(() => props.count > MAX_CSV_ORDERS_COUNT)
const tableTitle = computed(() => {
  if (props.isReceivedTab) {
    return i18n(translations.tableTitleReceived, {
      count: props.count,
    })
  }

  if (props.isPendingTab) {
    return i18n(translations.tableTitlePending, {
      count: props.count,
    })
  }

  if (props.isAllTab) {
    return i18n(translations.tableTitleAll, {
      count: props.count,
    })
  }

  return ''
})
const showCsvExport = computed(
  () => props.isAllTab && !KILL_REFURBISHER_LISTING_EXPENSIVE,
)
const canExportOrders = computed(() => {
  const {
    creationDateMin,
    creationDateMax,
    customer,
    sourcingOrderId,
    state,
    trackingNumber,
  } = props.filters

  return (
    (!!creationDateMin ||
      !!creationDateMax ||
      !!customer ||
      !!sourcingOrderId ||
      !!state ||
      !!trackingNumber) &&
    !tooMuchCSVOrders.value
  )
})

function redirect(orderId: string) {
  const redirectRoute = router.resolve({
    name: ROUTE_NAMES.BUYBACK.ORDERS.DETAILS,
    params: { id: orderId },
  })

  window.open(redirectRoute.href, '_blank', 'noopener noreferrer')
}

async function exportCSV() {
  isDownloading.value = true
  try {
    const response = await $httpFetch(exportSourcingOrders, {
      queryParams: {
        ...props.filters,
      },
    })
    const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const pom = document.createElement('a')

    pom.href = url
    pom.setAttribute('download', 'export.csv')
    pom.click()
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][ORDERS] Error getting orders CSV export', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    isDownloading.value = false
  }
}
</script>
